export interface CasaNativaRestaurant {
  name: string;
  tagline: string;
  address: string;
  phone: string;
  hours: string;
  demoLabel: "Entorno de demostración";
}

export interface CasaNativaMenuVariant {
  id: string;
  name: string;
  priceMinor: number;
  revision?: number;
  isDefault: boolean;
}

export interface CasaNativaMenuItem {
  id: string;
  canonicalItemId?: string;
  name: string;
  description: string;
  category: string;
  categoryId?: string;
  unitPriceMinor: number;
  currency: "ARS";
  available: boolean;
  availabilityReason: string | null;
  availableUntil: string | null;
  revision?: number;
  imageAssetId?: string | null;
  imageUrl: string | null;
  allergens: readonly string[];
  dietaryClaims: readonly string[];
  variants: readonly CasaNativaMenuVariant[];
  published: boolean;
}

export interface CasaNativaMenuPublication {
  status: "published" | "draft";
  currentVersionId?: string | null;
  versionNumber: number | null;
  publishedAt: string | null;
  draftRevision?: number;
  hasPendingChanges?: boolean;
  versions?: readonly {
    id: string;
    versionNumber: number;
    publishedAt: string;
    restoredFrom: string | null;
  }[];
}

export interface CasaNativaRecipeQuantity {
  amount: number;
  scale: number;
  unit: string;
}

export interface CasaNativaRecipeVersionComponent {
  id: string;
  kind: "ingredient" | "recipe_version";
  componentKey: string | null;
  label: string;
  quantity: CasaNativaRecipeQuantity;
  wasteFactorBasisPoints: number | null;
  notes: string | null;
  nestedRecipeVersionId: string | null;
  nestedRecipeName: string | null;
}

export interface CasaNativaRecipeVersion {
  id: string;
  versionNumber: number;
  status: string;
  effectiveFrom: string | null;
  publishedAt: string | null;
  createdAt: string;
  yieldDefinition: {
    output: CasaNativaRecipeQuantity;
    preparationUnit: string | null;
  };
  components: readonly CasaNativaRecipeVersionComponent[];
}

export interface CasaNativaExpectedConsumption {
  kind: "expected_consumption";
  rootRecipeVersionId: string;
  requestedOutput: CasaNativaRecipeQuantity;
  sourceVersionIds: readonly string[];
  derivedAt: string;
  ingredients: readonly {
    componentKey: string;
    label: string;
    quantity: CasaNativaRecipeQuantity;
    recipeVersionPath: readonly string[];
  }[];
}

export interface CasaNativaRecipe {
  itemId: string;
  recipeId: string;
  recipeVersionId: string;
  name: string;
  status: string;
  definitionStatus: string;
  revision: number;
  currentPublishedVersionId: string;
  output: CasaNativaRecipeQuantity;
  components: readonly {
    key: string;
    label: string;
    quantity: CasaNativaRecipeQuantity;
  }[];
  versions: readonly CasaNativaRecipeVersion[];
  expectedConsumption: CasaNativaExpectedConsumption;
}

export interface CasaNativaOrderLine {
  id: string;
  sourceItemId: string | null;
  sourceVariantId: string | null;
  name: string;
  variantName: string | null;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
  notes: string | null;
}

export interface CasaNativaOrder {
  id: string;
  customerId: string | null;
  customer: string;
  phone: string | null;
  address: string | null;
  fulfillmentType: string;
  requestedAt: string | null;
  notes: string | null;
  status: string;
  revision: number;
  totalMinor: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  lines: readonly CasaNativaOrderLine[];
}

export interface CasaNativaProductionWork {
  id: string;
  orderId: string;
  orderStatus: string | null;
  itemName: string;
  quantity: number;
  unit: string;
  status: string;
  revision: number;
  queuedAt: string;
  stationId: string | null;
  stationName: string | null;
  priority: number;
  blockedReason: string | null;
}

export interface CasaNativaProductionStation {
  id: string;
  name: string;
  active: boolean;
  revision: number;
}

export interface CasaNativaKdsProjection {
  owner: "gastro.production";
  generatedAt: string;
  cards: readonly CasaNativaProductionWork[];
}

export interface CasaNativaInventoryBalance {
  locationId: string;
  locationName: string;
  amount: number;
  unit: string;
  negative: boolean;
  latestMovementAt: string | null;
}

export interface CasaNativaInventoryItem {
  id: string;
  name: string;
  componentKey: string | null;
  amount: number;
  unit: string;
  balances: readonly CasaNativaInventoryBalance[];
}

export interface CasaNativaInventoryLocation {
  id: string;
  name: string;
  active: boolean;
  revision: number;
}

export interface CasaNativaInventoryMovement {
  id: string;
  inventoryItemId: string;
  itemName: string;
  locationId: string;
  locationName: string;
  kind: string;
  quantity: number;
  unit: string;
  signedBaseAmount: number;
  transferId: string | null;
  occurredAt: string;
  postedAt: string;
  reason: string | null;
  correlationId: string | null;
}

export interface CasaNativaInventoryCount {
  id: string;
  inventoryItemId: string;
  itemName: string;
  locationId: string;
  locationName: string;
  observed: number;
  expectedBeforeCount: number;
  variance: number;
  unit: string;
  reconciliationMovementId: string | null;
  countedAt: string;
  recordedAt: string;
}

export interface CasaNativaPurchaseNeed {
  id: string;
  status: string;
  revision: number;
  createdAt: string;
  lines: readonly {
    id: string;
    inventoryItemId: string;
    requested: number;
    unit: string;
    reason: string | null;
  }[];
}

export interface CasaNativaSourceEvidence {
  sourceKey: string;
  sourceRecordId: string | null;
  authority: "canonical" | "integrated" | "human_statement" | "derived";
  capturedAt: string;
}

export interface CasaNativaPurchasePriceEvidence {
  amountMinor: number;
  currency: string;
  pricePerQuantity: number;
  pricePerUnit: string;
  source: CasaNativaSourceEvidence;
  moneySource: CasaNativaSourceEvidence;
}

export interface CasaNativaPurchaseOrder {
  id: string;
  needId: string | null;
  supplier: string;
  supplierId: string;
  supplierOwner: string;
  supplierSource: CasaNativaSourceEvidence;
  status: string;
  revision: number;
  createdAt: string;
  source: CasaNativaSourceEvidence;
  lines: readonly { id: string; inventoryItemId: string; ordered: number; unit: string; supplierQuote: CasaNativaPurchasePriceEvidence | null }[];
}

export interface CasaNativaGoodsReceipt {
  id: string;
  purchaseOrderId: string;
  receivedAt: string;
  recordedAt: string;
  source: CasaNativaSourceEvidence;
  authority: { actorKind: string; actorId: string };
  lines: readonly {
    id: string; purchaseOrderLineId: string; inventoryItemId: string; received: number; accepted: number; rejected: number; unit: string; rejectionReason: string | null; acceptedPriceMinor: number | null; acceptedPurchasePrice: CasaNativaPurchasePriceEvidence | null;
    progress: { ordered: number; acceptedBefore: number; outstandingBefore: number; acceptedThisReceipt: number; acceptedAfter: number; outstandingAfter: number; unit: string; overReceived: boolean };
  }[];
}

export interface CasaNativaCostingItem {
  itemId: string;
  itemName: string;
  status: string;
  totalCostMinor: number | null;
  contributionMinor: number | null;
  foodCostBasisPoints: number | null;
  accountingCompleteness?: "not_claimed";
  costElementsIncluded?: readonly string[];
  issues: readonly string[];
}

export interface CasaNativaQualityAction {
  id: string;
  description: string;
  ownerRef: string;
  status: string;
  revision: number;
}

export interface CasaNativaQualityIncident {
  id: string;
  title: string;
  severity: string;
  status: string;
  revision: number;
  openedAt: string;
  sourceOwner: string | null;
  sourceId: string | null;
  actions: readonly CasaNativaQualityAction[];
}

export interface CasaNativaQualityCheck {
  id: string;
  definitionKey: string;
  result: string;
  executedAt: string;
  externalCompliance: "not_assessed";
}

export interface CasaNativaDelivery {
  id: string;
  orderId: string;
  customer: string;
  address: string | null;
  status: string;
  revision: number;
  assignedTo: string | null;
  createdAt: string;
  updatedAt: string;
  proofOfDelivery: string | null;
}

export interface CasaNativaSchedulingSlot {
  startsAt: string;
  endsAt: string;
  capacity: number;
  available: boolean;
  reason: string | null;
}

export interface CasaNativaSchedulingAppointment {
  id: string;
  status: string;
  customerId: string | null;
  customerName: string | null;
  serviceId: string;
  serviceName: string | null;
  resourceId: string | null;
  resourceName: string | null;
  locationId: string | null;
  startsAt: string;
  endsAt: string;
}

export interface CasaNativaScheduling {
  owner: "scheduling";
  readOnly: true;
  service: { id: string; organizationId: string; name: string; durationMinutes: number; active: boolean; description?: string | null; createdAt?: string; updatedAt?: string };
  resource: { id: string; organizationId: string; name: string; displayName?: string | null; active: boolean; createdAt?: string; updatedAt?: string };
  availability: { timezone: string; slots: readonly CasaNativaSchedulingSlot[] };
  appointments: readonly CasaNativaSchedulingAppointment[];
  boundaries: {
    reservationSemantics: "appointment_foundation";
    partySize: "not_modeled_in_scheduling_v1";
    waitlist: "not_modeled_in_scheduling_v1";
    physicalTableSession: "not_owned_by_scheduling";
  };
}

export interface CasaNativaCustomerIdentity {
  id: string;
  type: string;
  provider: string | null;
  externalId: string;
  verified: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CasaNativaCustomer360 {
  readOnly: true;
  generatedAt: string;
  customer: {
    id: string;
    displayName: string | null;
    phone: string | null;
    email: string | null;
    notes: string | null;
    sourceChannel: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
  };
  summary: {
    totalAppointments: number | null;
    confirmedAppointments: number | null;
    cancelledAppointments: number | null;
    noShowAppointments: number | null;
    lastAppointmentAt: string | null;
    nextAppointmentAt: string | null;
    commercialState: string;
  };
  nextAction: { code: string; label: string; reason: string };
  nextAppointment: unknown | null;
  appointments: readonly unknown[];
  conversation: unknown | null;
  schedulingAvailability: { status: "available" | "unavailable"; reason?: string };
  conversationAvailability: { status: "available" | "unavailable"; reason?: string };
  recentInteractions: readonly unknown[];
  limits: {
    crmEditable: boolean;
    inboxRealtime: boolean;
    aiGeneratedInsights: boolean;
  };
}

export interface CasaNativaCustomer {
  id: string;
  name: string;
  phone: string | null;
  identities: readonly CasaNativaCustomerIdentity[];
  customer360: CasaNativaCustomer360;
  orderCount: number;
  completedOrderCount: number;
  totalSpendMinor: number;
  lastOrderAt: string | null;
  lastCompletedAt: string | null;
  favoriteProduct: string | null;
}

export interface CasaNativaAnalyticsMetric {
  status: "measured" | "unavailable" | "invalid";
  value: number | null;
  evidenceRefs: readonly string[];
}

export interface CasaNativaCoreExperience {
  schemaVersion: 1;
  profile: {
    profileId: string;
    displayName: string;
    shortDescription: string | null;
    addressLabel: string | null;
    contacts: readonly { kind: string; value: string; label: string | null }[];
    openingHours: readonly unknown[];
    publicLinks: readonly string[];
    revision: number;
    openingHoursGap: "split_shift_not_representable_in_profile_v1";
  };
  publicSite: {
    siteId: string;
    locale: string;
    publicationRevision: number;
    publishedAt: string;
    sections: readonly {
      kind: string;
      sectionKey: string;
      label: string | null;
      availability: string | null;
      publicUrl: string | null;
      renderRef: string | null;
    }[];
  };
  qrEntry: {
    entryId: string;
    status: string;
    revision: number;
    targetKind: string;
    targetSiteId: string | null;
    resolvable: boolean;
    resolvedKind: string | null;
  };
}

export interface CasaNativaReputation {
  schemaVersion: 1;
  fixture: "casa-nativa-reputation-reference-v1";
  externalActions: "blocked";
  providerMode: { google: "mock"; ai: "mock_only" };
  reviewCount: number;
  averageRating: number;
  reviews: readonly {
    id: string;
    author: string;
    rating: number;
    text: string;
    publishedAt: string;
    status: string;
    sentiment: string | null;
    riskLevel: string | null;
    requiresHumanReview: boolean;
    response: { body: string; tone: string; status: string } | null;
  }[];
  riskAlerts: readonly {
    id: string;
    reviewId: string;
    severity: string;
    title: string;
    status: string;
    createdAt: string;
  }[];
}

export interface CasaNativaAdminSnapshot {
  schemaVersion: 2;
  runtime: "canonical-composition";
  stateMode: "isolated-reference";
  externalActions: "blocked";
  organizationId: string;
  locationId: string;
  restaurant: CasaNativaRestaurant;
  menu: readonly CasaNativaMenuItem[];
  publicMenu: readonly CasaNativaMenuItem[];
  menuPublication: CasaNativaMenuPublication;
  menuSource: {
    owner: "menu";
    runtime: "canonical-menu-reference";
    localId: string;
  };
  recipes: readonly CasaNativaRecipe[];
  orders: readonly CasaNativaOrder[];
  productionStations: readonly CasaNativaProductionStation[];
  production: readonly CasaNativaProductionWork[];
  kds: CasaNativaKdsProjection;
  inventoryLocations: readonly CasaNativaInventoryLocation[];
  inventory: readonly CasaNativaInventoryItem[];
  inventoryMovements: readonly CasaNativaInventoryMovement[];
  inventoryCounts: readonly CasaNativaInventoryCount[];
  procurement: {
    needs: readonly CasaNativaPurchaseNeed[];
    orders: readonly CasaNativaPurchaseOrder[];
    receipts: readonly CasaNativaGoodsReceipt[];
  };
  costing: { currency: "ARS"; items: readonly CasaNativaCostingItem[] };
  quality: { checks: readonly CasaNativaQualityCheck[]; incidents: readonly CasaNativaQualityIncident[] };
  delivery: readonly CasaNativaDelivery[];
  scheduling: CasaNativaScheduling;
  customers: readonly CasaNativaCustomer[];
  analytics: {
    completedOrders: CasaNativaAnalyticsMetric;
    createdOrders: CasaNativaAnalyticsMetric;
    completedRevenueMinor: number;
    completedUnits: number;
    operational: {
      openPurchaseNeeds: number;
      rejectedReceiptUnits: number;
      openQualityIncidents: number;
      activeDeliveries: number;
    };
    currency: "ARS";
  };
  coreExperience: CasaNativaCoreExperience;
  reputation: CasaNativaReputation;
}

export interface CasaNativaPublicSnapshot {
  restaurant: CasaNativaRestaurant;
  menu: readonly CasaNativaMenuItem[];
  menuPublication: CasaNativaMenuPublication;
}

export interface CasaNativaPublicOrderResult extends CasaNativaPublicSnapshot {
  order: Pick<CasaNativaOrder, "id" | "status" | "fulfillmentType" | "requestedAt" | "notes" | "totalMinor" | "createdAt" | "lines">;
}

interface ApiEnvelope<T> {
  data?: T;
  error?: { code?: string; message?: string };
  requestId?: string;
}

type Fetcher = typeof fetch;

export class CasaNativaClientError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = "CasaNativaClientError";
  }
}

const ADMIN_BASE = "/api/v1/platform/demos/gastronomy/casa-nativa";
const PUBLIC_BASE = `${ADMIN_BASE}/public`;
const ORDER_ID = /^cd_ord_[A-Za-z0-9._:-]{1,160}$/;
const PRODUCTION_WORK_ID = /^cd_gprod_work_[A-Za-z0-9._:-]{1,160}$/;
const DELIVERY_ID = /^cd_gdelivery_[A-Za-z0-9._:-]{1,160}$/;
const QUALITY_INCIDENT_ID = /^cd_gquality_inc_[A-Za-z0-9._:-]{1,160}$/;
const QUALITY_ACTION_ID = /^cd_gquality_action_[A-Za-z0-9._:-]{1,160}$/;
const MENU_ITEM_ID = /^[A-Za-z0-9._:-]{1,160}$/;
const INVENTORY_ITEM_ID = /^cd_ginv_item_[A-Za-z0-9._:-]{1,160}$/;
const INVENTORY_LOCATION_ID = /^cd_ginv_loc_[A-Za-z0-9._:-]{1,160}$/;

export class CasaNativaClient {
  constructor(private readonly fetcher: Fetcher = globalThis.fetch.bind(globalThis)) {}

  publicState(): Promise<CasaNativaPublicSnapshot> {
    return this.request<CasaNativaPublicSnapshot>(PUBLIC_BASE, { credentials: "same-origin" });
  }

  createPublicOrder(input: {
    contactName: string;
    phone?: string;
    fulfillmentType: "pickup" | "delivery" | "dine_in";
    address?: string;
    requestedAt?: string;
    notes?: string;
    items: readonly { itemId: string; variantId?: string; quantity: number; notes?: string }[];
  }): Promise<CasaNativaPublicOrderResult> {
    return this.request<CasaNativaPublicOrderResult>(`${PUBLIC_BASE}/orders`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  adminState(): Promise<CasaNativaAdminSnapshot> {
    return this.request<CasaNativaAdminSnapshot>(ADMIN_BASE, { credentials: "include" });
  }

  reset(): Promise<CasaNativaAdminSnapshot> {
    return this.adminMutate(`${ADMIN_BASE}/reset`, "POST", {});
  }

  updateMenuItem(itemId: string, input: { unitPriceMinor?: number; available?: boolean }): Promise<CasaNativaAdminSnapshot> {
    if (!MENU_ITEM_ID.test(itemId)) throw new CasaNativaClientError(400, "menu_item_id_invalid", "Producto inválido.");
    return this.adminMutate(`${ADMIN_BASE}/menu/${encodeURIComponent(itemId)}`, "PATCH", input);
  }

  acceptOrder(orderId: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(orderId, ORDER_ID, "Pedido inválido.");
    return this.adminMutate(`${ADMIN_BASE}/orders/${encodeURIComponent(orderId)}/accept`, "POST", {});
  }

  completeOrder(orderId: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(orderId, ORDER_ID, "Pedido inválido.");
    return this.adminMutate(`${ADMIN_BASE}/orders/${encodeURIComponent(orderId)}/complete`, "POST", {});
  }

  startProduction(productionWorkId: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(productionWorkId, PRODUCTION_WORK_ID, "Trabajo de cocina inválido.");
    return this.adminMutate(`${ADMIN_BASE}/production/${encodeURIComponent(productionWorkId)}/start`, "POST", {});
  }

  readyProduction(productionWorkId: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(productionWorkId, PRODUCTION_WORK_ID, "Trabajo de cocina inválido.");
    return this.adminMutate(`${ADMIN_BASE}/production/${encodeURIComponent(productionWorkId)}/ready`, "POST", {});
  }

  recordInventoryWaste(itemId: string, input: { locationId: string; amount: number; reason?: string }): Promise<CasaNativaAdminSnapshot> {
    this.assertId(itemId, INVENTORY_ITEM_ID, "Insumo inválido.");
    this.assertId(input.locationId, INVENTORY_LOCATION_ID, "Ubicación inválida.");
    return this.adminMutate(`${ADMIN_BASE}/inventory/${encodeURIComponent(itemId)}/waste`, "POST", input);
  }

  recordInventoryCount(itemId: string, input: { locationId: string; observed: number }): Promise<CasaNativaAdminSnapshot> {
    this.assertId(itemId, INVENTORY_ITEM_ID, "Insumo inválido.");
    this.assertId(input.locationId, INVENTORY_LOCATION_ID, "Ubicación inválida.");
    return this.adminMutate(`${ADMIN_BASE}/inventory/${encodeURIComponent(itemId)}/count`, "POST", input);
  }

  transferInventory(itemId: string, input: { fromLocationId: string; toLocationId: string; amount: number; reason?: string }): Promise<CasaNativaAdminSnapshot> {
    this.assertId(itemId, INVENTORY_ITEM_ID, "Insumo inválido.");
    this.assertId(input.fromLocationId, INVENTORY_LOCATION_ID, "Ubicación de origen inválida.");
    this.assertId(input.toLocationId, INVENTORY_LOCATION_ID, "Ubicación de destino inválida.");
    return this.adminMutate(`${ADMIN_BASE}/inventory/${encodeURIComponent(itemId)}/transfer`, "POST", input);
  }

  deliveryAction(id: string, action: "assign" | "pickup" | "transit" | "deliver"): Promise<CasaNativaAdminSnapshot> {
    this.assertId(id, DELIVERY_ID, "Entrega inválida.");
    return this.adminMutate(`${ADMIN_BASE}/delivery/${encodeURIComponent(id)}/${action}`, "POST", {});
  }

  advanceQualityIncident(id: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(id, QUALITY_INCIDENT_ID, "Incidente inválido.");
    return this.adminMutate(`${ADMIN_BASE}/quality/incidents/${encodeURIComponent(id)}/advance`, "POST", {});
  }

  advanceQualityAction(id: string): Promise<CasaNativaAdminSnapshot> {
    this.assertId(id, QUALITY_ACTION_ID, "Acción correctiva inválida.");
    return this.adminMutate(`${ADMIN_BASE}/quality/actions/${encodeURIComponent(id)}/advance`, "POST", {});
  }

  private assertId(value: string, pattern: RegExp, message: string): void {
    if (!pattern.test(value)) throw new CasaNativaClientError(400, "reference_id_invalid", message);
  }

  private async csrf(): Promise<string> {
    const response = await this.fetcher("/api/v1/platform/auth/csrf", { credentials: "include" });
    const body = await response.json().catch(() => ({})) as ApiEnvelope<{ authenticated: boolean; csrfToken: string }>;
    if (!response.ok || !body.data?.csrfToken || body.data.authenticated !== true) {
      throw new CasaNativaClientError(response.status, body.error?.code ?? "csrf_required", body.error?.message ?? "No se pudo validar la sesión.");
    }
    return body.data.csrfToken;
  }

  private async adminMutate(path: string, method: "POST" | "PATCH", body: Readonly<Record<string, unknown>>): Promise<CasaNativaAdminSnapshot> {
    const csrfToken = await this.csrf();
    return this.request<CasaNativaAdminSnapshot>(path, {
      method,
      credentials: "include",
      headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
      body: JSON.stringify(body),
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.fetcher(path, init);
    const body = await response.json().catch(() => ({})) as ApiEnvelope<T>;
    if (!response.ok || !body.data) {
      throw new CasaNativaClientError(response.status, body.error?.code ?? `casa_nativa_${response.status}`, body.error?.message ?? "Casa Nativa no pudo completar la acción.");
    }
    return body.data;
  }
}
